import { useEffect, useState, useRef, useContext } from "@wordpress/element";
import WidgetContext from "@/context/widget-context";
import { useLicense } from "@/context/license-context";
import { isPremiumFeature } from "@/constant/premium-features";
import { useTranslation } from "@/hooks/use-translation";

import {
	cn,
	getContrastTextColor,
	stepBasedFeatures,
	stepLabels,
	accessibilityProfile,
} from "@/lib/utils";
import { useFeatureToggle } from "@/hooks/use-feayure-toggle";
import { featureConfig } from "@/hooks/use-feayure-toggle";
import { CheckCheck, CircleCheck, XCircle } from "lucide-react";

import { AnimatedContainer } from "@/motions/animated-container";
import { AnimatedItem } from "@/motions/animated-item";
import { LabelTooltip } from "@/components/label-tooltip";
import { Badge } from "@/components/ui/badge";
import { useMediaQuery } from "@wordpress/compose";
const AccessibilityProfile = ({ theme }) => {
	const { __ } = useTranslation();
	const { applyProfile, activeProfile } = useFeatureToggle();
	const widgetSettings = useContext(WidgetContext) || {};
	const license = useLicense ? useLicense() : null;
	const hasFeature = (label) =>
		license?.hasFeature ? license.hasFeature(label) : false;
	const isMobile = useMediaQuery("(max-width: 768px)");
	// If this section is premium and license doesn't include it, do not render on the frontend
	const isLocked =
		isPremiumFeature("accessibilityProfile") &&
		!hasFeature("Accessibility Profile");

	// If locked, proactively clear any profile-enabled features lingering from previous sessions
	useEffect(() => {
		if (!isLocked) return;
		try {
			const activeRaw = localStorage.getItem(
				"accessibility-widget-active-features",
			);
			const active = activeRaw ? JSON.parse(activeRaw) : [];
			// Determine union of all features present in defined profiles
			const profileFeatureKeys = new Set();
			(accessibilityProfile || []).forEach((p) => {
				(p.features || []).forEach((f) => profileFeatureKeys.add(f.key));
			});
			const filtered = active.filter((k) => !profileFeatureKeys.has(k));
			if (filtered.length !== active.length) {
				localStorage.setItem(
					"accessibility-widget-active-features",
					JSON.stringify(filtered),
				);
				if (typeof window !== "undefined") {
					window.dispatchEvent(new Event("accessibility-features-changed"));
				}
			}
		} catch {}
	}, [isLocked]);

	if (isLocked) return null;

	// Build a set of allowed feature keys from backend-provided feature groups
	const allowedFeatureKeys = new Set();
	(widgetSettings.features || []).forEach((group) => {
		(group.settings || []).forEach((setting) => {
			if (!setting.enabled) return;
			if (isPremiumFeature(setting.key) && !hasFeature(setting.label)) return;
			allowedFeatureKeys.add(setting.key);
		});
	});

	return (
		<AnimatedContainer>
			<div>
				<LabelTooltip
					tooltipClassName="!w-[250px]"
					iconClassName="zn:h-4 zn:w-4"
					labelClassName={cn(" zn:text-default-800 zn:font-medium ", {
						"zn:md:!text-lg zn:!text-base zn:!leading-none":
							theme === "default",
						"zn:!text-base zn:!leading-none": theme === "classic",
					})}
					label={__("Accessibility Profiles", "accessibly-wp")}
					tooltip={__(
						"Select a profile that best fits your accessibility needs",
						"accessibly-wp",
					)}
				/>
			</div>
			<div
				className={cn(
					"zn:grid zn:grid-cols-1 zn:md:grid-cols-2 zn:md:gap-4 zn:gap-2 zn:md:!mt-7 zn:!mt-5",
					{
						"zn:!mt-5": theme === "default",
					},
				)}
			>
				{accessibilityProfile?.map((item, index) => {
					const IconComponent = item.icon;
					const isActive = activeProfile === item.id;
					// Filter out disallowed features for this profile
					const filteredFeatures = (item.features || []).filter((f) =>
						allowedFeatureKeys.has(f.key),
					);
					const disabled = filteredFeatures.length === 0; // nothing applicable
					return (
						<AnimatedItem key={item.id} delay={index * 0.05}>
							<div
								onClick={() => {
									if (disabled) return; // ignore if no active features
									// Apply a shallow copy with filtered features to avoid enabling disallowed
									applyProfile({ ...item, features: filteredFeatures });
								}}
								className={cn(
									"zn:!bg-white zn:cursor-pointer zn:!py-3.5 zn:!px-3 zn:rounded-md  zn:hover:!border-primary-icon zn:!border zn:transition zn:relative zn:!border-default-200 ",
									{
										"zn:!border-primary-icon zn:ring-2 zn:ring-primary-icon/40 ":
											isActive,
										"zn:opacity-50 zn:cursor-not-allowed":
											disabled && !isActive,
									},
								)}
							>
								<div
									className={cn("zn:flex zn:items-center  zn:relative", {
										"zn:gap-2": theme === "default",
										"zn:gap-3": theme === "classic",
									})}
								>
									{isActive && theme === "classic" && (
										<span className="zn:absolute zn:top-1 zn:end-1 zn:text-[10px] zn:px-1.5 zn:py-0.5  zn:text-white  zn:rounded-sm zn:tracking-wide">
											<CircleCheck className="zn:w-5 zn:h-5 zn:fill-primary-icon" />
										</span>
									)}
									{isActive && theme === "default" && isMobile && (
										<span className="zn:absolute zn:top-1 zn:end-1 zn:text-[10px] zn:px-1.5 zn:py-0.5  zn:text-white  zn:rounded-sm zn:tracking-wide">
											<CircleCheck className="zn:w-5 zn:h-5 zn:fill-primary-icon" />
										</span>
									)}
									<div className="zn:flex-shrink-0">
										<IconComponent
											className={cn("zn:w-6 zn:h-6", {
												"zn:text-primary-icon": isActive,
												"zn:text-gray-400": disabled && !isActive,
												"zn:text-gray-700": !isActive && !disabled,
											})}
										/>
									</div>
									<span
										className={cn("zn:!font-medium zn:!text-[15px]", {
											"zn:text-primary-icon": isActive,
											"zn:text-gray-400": disabled && !isActive,
											"zn:text-gray-900": !isActive && !disabled,
										})}
									>
										{__(item.title, "accessibly-wp")}
									</span>
								</div>
							</div>
						</AnimatedItem>
					);
				})}
			</div>
		</AnimatedContainer>
	);
};

export default AccessibilityProfile;
